import React, { useState, useEffect, useRef } from 'react';
import ProgressBar from './ProgressBar';
import Timer from './Timer';
import { playSound } from '../../utils/soundManager';
import { QUIZ_MODES, getTimerSettings, calculateTimeBonus } from '../../utils/timerManager';

const QuizCard = ({ category, question, questionNumber, totalQuestions, onAnswer, score, quizMode, difficulty }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const questionStartTime = useRef(Date.now());

  const timerSettings = getTimerSettings(difficulty);
  const isTimedMode = quizMode === QUIZ_MODES.TIMED;

  useEffect(() => {
    // Reset for new question
    questionStartTime.current = Date.now();
    setTimeRemaining(isTimedMode ? timerSettings.timePerQuestion : null);
  }, [question, isTimedMode, timerSettings.timePerQuestion]);

  const handleAnswerClick = (answer) => {
    if (isAnswered) return;
    
    const timeSpent = Math.floor((Date.now() - questionStartTime.current) / 1000);
    
    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    // Wait a moment before moving to next question
    setTimeout(() => {
      // Calculate time bonus if in timed mode
      let bonus = 0;
      if (isTimedMode && timeRemaining !== null) {
        bonus = calculateTimeBonus(timeRemaining, timerSettings.timePerQuestion, timerSettings.bonusPoints);
      }
      
      onAnswer(answer, timeSpent, bonus);
      setSelectedAnswer(null);
      setIsAnswered(false);
    }, 1500);
  };

  const handleTimeUp = () => {
    if (!isAnswered) {
      setIsAnswered(true);
      setTimeout(() => {
        onAnswer(null, timerSettings.timePerQuestion, 0); // null answer means time's up
        setSelectedAnswer(null);
        setIsAnswered(false);
      }, 1000);
    }
  };

  const getButtonClass = (option) => {
    if (!isAnswered) return 'option-button hover-lift btn-press';
    
    if (option === question.correct) {
      return 'option-button correct animate-pulse';
    }
    
    if (option === selectedAnswer && option !== question.correct) {
      return 'option-button incorrect animate-shake';
    }
    
    return 'option-button';
  };

  return (
    <div className="quiz-container animate-fadeIn">
      <ProgressBar 
        current={questionNumber} 
        total={totalQuestions}
        score={score}
      />

      {isTimedMode && (
        <Timer
          totalTime={timerSettings.timePerQuestion}
          warningTime={timerSettings.warningTime}
          onTimeUp={handleTimeUp}
          isPaused={isAnswered}
          showProgress={true}
        />
      )}
      
      <div className="quiz-card">
        <div className="question-header">
          <span className="question-number">Question {questionNumber} of {totalQuestions}</span>
          <span className="current-score">🌟 Score: {score}</span>
        </div>
        
        <div className="question-content">
          <div className="question-emoji animate-bounce">{question.emoji}</div>
          <h2 className="question-text">{question.question}</h2>
        </div>
        
        <div className="options-grid">
          {question.options.map((option, index) => (
            <button
              key={index}
              className={getButtonClass(option)}
              onClick={() => handleAnswerClick(option)}
              disabled={isAnswered}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
        
        {isAnswered && (
          <div className={`feedback animate-scaleIn ${
            selectedAnswer === question.correct ? 'feedback-correct' : 'feedback-incorrect'
          }`}>
            {selectedAnswer === question.correct ? (
              <>
                <span className="feedback-icon">✅</span>
                <span>Great job! That's correct!</span>
              </>
            ) : (
              <>
                <span className="feedback-icon">❌</span>
                <span>Oops! The correct answer is {question.correct}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizCard;
